import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { fetchWithAuth } from '../util/fetchWithAuth';
import { formatCurrency } from '../util/formatCurrency';

type ReconRow = { label: string; values: number[] };
type ReconBlock = { label: string; rows: Record<string, ReconRow> };

type ReconResponse = {
  months: string[];
  blocks: {
    income: ReconBlock;
    item_spend: ReconBlock;
    peripheries: ReconBlock;
    net: ReconBlock;
  };
};

type BlockConfig = {
  key: keyof ReconResponse['blocks'];
  accent: string;
  rowOrder: string[];
  baseRow: string;
  coloredRows: string[];
  neutralRows: string[];
  percentRows: string[];
};

const BLOCKS: BlockConfig[] = [
  {
    key: 'income',
    accent: 'bg-emerald-100 text-emerald-900',
    rowOrder: ['order_totals', 'extra_income', 'cash_refunds', 'expected', 'received', 'difference', 'difference_pct'],
    baseRow: 'expected',
    coloredRows: ['difference'],
    neutralRows: [],
    percentRows: ['difference_pct'],
  },
  {
    key: 'item_spend',
    accent: 'bg-orange-100 text-orange-900',
    rowOrder: ['app', 'bank', 'difference', 'difference_pct'],
    baseRow: 'app',
    coloredRows: ['difference'],
    neutralRows: [],
    percentRows: ['difference_pct'],
  },
  {
    key: 'peripheries',
    accent: 'bg-yellow-100 text-yellow-900',
    rowOrder: ['app', 'bank', 'monthly_difference', 'cumulative_difference'],
    baseRow: 'app',
    coloredRows: ['cumulative_difference'],
    neutralRows: ['monthly_difference'],
    percentRows: [],
  },
  {
    key: 'net',
    accent: 'bg-indigo-100 text-indigo-900',
    rowOrder: ['app', 'bank', 'monthly_difference', 'cumulative_difference'],
    baseRow: 'app',
    coloredRows: ['cumulative_difference'],
    neutralRows: ['monthly_difference'],
    percentRows: [],
  },
];

const SUBTOTAL_ROWS = new Set(['expected', 'received', 'app', 'bank']);

const DEFAULT_MONTH_SPAN = 6;
const MAX_MONTH_SPAN = 12;

const toMonthString = (date: Date): string =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

const addMonths = (yyyymm: string, delta: number): string => {
  const [year, month] = yyyymm.split('-').map(Number);
  return toMonthString(new Date(year, month - 1 + delta, 1));
};

const defaultRange = () => {
  const now = new Date();
  const to = toMonthString(now);
  const from = addMonths(to, -(DEFAULT_MONTH_SPAN - 1));
  return { from, to };
};

const formatMonthLabel = (yyyymm: string): string => {
  const [year, month] = yyyymm.split('-').map(Number);
  if (!year || !month) return yyyymm;
  return new Date(year, month - 1, 1).toLocaleString('default', { month: 'short', year: '2-digit' });
};

// Difference is judged relative to the block's base figure: within 1% reads as
// reconciled, up to 5% as a minor gap, beyond that as worth investigating.
const WITHIN_TOLERANCE_RATIO = 0.01;
const MINOR_GAP_RATIO = 0.05;
const ZERO_EPSILON = 0.005;

const colourClass = (value: number, base: number): string => {
  const ratio = base ? Math.abs(value) / Math.abs(base) : (Math.abs(value) < ZERO_EPSILON ? 0 : 1);
  if (ratio <= WITHIN_TOLERANCE_RATIO) return 'bg-green-100 text-green-800';
  if (ratio <= MINOR_GAP_RATIO) return 'bg-amber-100 text-amber-800';
  return 'bg-red-100 text-red-800';
};

const FinanceReconciliation: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { from: defaultFrom, to: defaultTo } = defaultRange();
  const [from, setFrom] = useState<string>(searchParams.get('from') || defaultFrom);
  const [to, setTo] = useState<string>(searchParams.get('to') || defaultTo);
  const [data, setData] = useState<ReconResponse | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setSearchParams(prev => {
      prev.set('from', from);
      prev.set('to', to);
      return prev;
    }, { replace: true });
  }, [from, to, setSearchParams]);

  useEffect(() => {
    const load = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const query = new URLSearchParams({ from, to });
        const res = await fetchWithAuth(`/finance_reconciliation?${query.toString()}`);
        if (!res.ok) {
          throw new Error(`Request failed with status ${res.status}`);
        }
        setData(await res.json());
      } catch (err) {
        console.error('Error loading order reconciliation:', err);
        setError('Failed to load the order reconciliation.');
        setData(null);
      } finally {
        setIsLoading(false);
      }
    };

    load();
  }, [from, to]);

  const months = useMemo(() => data?.months ?? [], [data]);

  const formatCell = (value: number, isPercent: boolean): string =>
    isPercent ? `${value.toFixed(1)}%` : formatCurrency(value);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Order Reconciliation</h1>
        <p className="text-gray-600">What the app says happened on orders vs what the bank shows — income and spend, month by month.</p>
      </div>

      <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 mb-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 items-end">
          <div>
            <label htmlFor="from" className="block text-sm font-medium text-gray-700 mb-1">From</label>
            <input
              type="month"
              id="from"
              value={from}
              min={addMonths(to, -(MAX_MONTH_SPAN - 1))}
              max={to}
              onChange={(e) => setFrom(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label htmlFor="to" className="block text-sm font-medium text-gray-700 mb-1">To</label>
            <input
              type="month"
              id="to"
              value={to}
              min={from}
              max={toMonthString(new Date())}
              onChange={(e) => setTo(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <button
              type="button"
              onClick={() => {
                const range = defaultRange();
                setFrom(range.from);
                setTo(range.to);
              }}
              className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Reset to last {DEFAULT_MONTH_SPAN} months
            </button>
          </div>
        </div>
        <p className="text-xs text-gray-500 mt-2">Maximum range: {MAX_MONTH_SPAN} months.</p>
      </div>

      {error && (
        <div className="mb-4 px-4 py-3 bg-red-50 border border-red-200 text-red-800 rounded">
          {error}
        </div>
      )}

      {isLoading && !data && (
        <div className="text-gray-600">Loading…</div>
      )}

      {data && (
        <div className={`space-y-8 transition-opacity ${isLoading ? 'opacity-60' : ''}`}>
          {BLOCKS.map((config) => {
            const block = data.blocks[config.key];
            if (!block) return null;
            const baseValues = block.rows[config.baseRow]?.values ?? [];
            // Cumulative rows are running totals, so colour them against the
            // running base rather than a single month's figure (which would
            // make a modest cumulative gap look catastrophic, and vice-versa).
            const cumulativeBase: number[] = [];
            baseValues.reduce((acc, v, i) => { cumulativeBase[i] = acc + v; return cumulativeBase[i]; }, 0);

            return (
              <div key={config.key} className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-x-auto">
                <table className="min-w-full border-collapse">
                  <thead>
                    <tr>
                      <th className={`sticky left-0 z-10 px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide border-b border-r border-gray-200 ${config.accent}`}>
                        {block.label}
                      </th>
                      {months.map((m) => (
                        <th
                          key={`${config.key}-th-${m}`}
                          className={`px-4 py-2 text-right text-xs font-semibold uppercase tracking-wide border-b border-gray-200 whitespace-nowrap ${config.accent}`}
                        >
                          {formatMonthLabel(m)}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {config.rowOrder.map((rowKey) => {
                      const row = block.rows[rowKey];
                      if (!row) return null;
                      const isPercent = config.percentRows.includes(rowKey);
                      const isColoured = config.coloredRows.includes(rowKey);
                      const isNeutral = config.neutralRows.includes(rowKey);
                      const isSubtotal = SUBTOTAL_ROWS.has(rowKey);
                      const isSupporting = !isColoured && !isNeutral && !isSubtotal && !isPercent;

                      return (
                        <tr key={`${config.key}-${rowKey}`} className={isSubtotal ? 'bg-gray-50' : 'hover:bg-gray-50'}>
                          <td
                            className={`sticky left-0 z-10 px-4 py-2 text-sm whitespace-nowrap border-r border-gray-200 ${
                              isSubtotal ? 'font-semibold bg-gray-50 text-gray-900' : 'bg-white'
                            } ${isSupporting ? 'text-gray-500' : 'text-gray-800'}`}
                          >
                            {row.label}
                          </td>
                          {row.values.map((value, idx) => {
                            let cellClass = 'text-gray-900';
                            if (isColoured) {
                              const base = rowKey.startsWith('cumulative') ? cumulativeBase[idx] : baseValues[idx];
                              cellClass = colourClass(value, base ?? 0);
                            } else if (isNeutral) cellClass = 'bg-gray-100 text-gray-500';
                            else if (isSupporting) cellClass = 'text-gray-500';

                            return (
                              <td
                                key={`${config.key}-${rowKey}-${months[idx]}`}
                                className={`px-4 py-2 text-right text-sm tabular-nums whitespace-nowrap ${cellClass} ${
                                  isSubtotal ? 'font-semibold' : ''
                                }`}
                              >
                                {formatCell(value, isPercent)}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            );
          })}

          <div className="text-xs text-gray-500 leading-relaxed max-w-3xl">
            <p className="mb-1">
              <strong>Income & item spend</strong> reconcile cleanly month to month (timing aside). A small bank shortfall on
              income is normal — Yoco deducts a card fee before deposit.
            </p>
            <p>
              <strong>Peripheries</strong> (doc fee + VAT, paid as border tax / courier-customs) are lumpy: courier invoices
              often bundle 2–3 months, so the monthly difference swings — watch the <strong>cumulative difference</strong>,
              which converges as bundled invoices clear. Order figures are dated when the order was marked paid; bank lines by
              transaction date.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default FinanceReconciliation;
